import normalize from "json-api-normalizer";

import get from "lodash/get";

import NotificationService from "../services/notification.service";
import {
  BACKEND_PROCESSING_ONGOING,
  FETCH_IDS_START,
  FETCH_IDS_SUCCESS,
  FETCH_SUCCESS,
  FETCH_FAILED,
  FETCH_START
} from "../actions";
import { UPDATE_RESOURCE_FIELDS } from "./types";
import { INTERNAL_RESOURCE_TYPE, INVOICE_SERVICES, INVOICE_STATUS } from "../components/Invoices/constants";
import InternalInvoicesService from "../services/internal-invoices.service";

export const fetchInvoices = (params, service = INVOICE_SERVICES.INTERNAL_INVOICES, options = {}) => async (
  dispatch,
  getState
) => {
  options = { performRequest: false, ...options };

  let invoiceService, resourceType;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
    resourceType = INTERNAL_RESOURCE_TYPE;
  }

  params.page = { number: params.page.number || 1, size: 15 };

  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_START, payload: { page: params.page.number } });
  try {
    const result = await invoiceService.all(tenantSlug, params);
    const data = normalize(result.data, { endpoint: resourceType });

    const totalNumberOfInvoicesData = await invoiceService.totalNumberOfInvoices(tenantSlug, params);
    const totalNumberOfInvoices = totalNumberOfInvoicesData.data.total;

    dispatch({
      type: FETCH_SUCCESS,
      payload: { ...data, page: params.page.number },
      totalResourceNumber: totalNumberOfInvoices,
      service: service,
      status: params.filter.status
    });
  } catch (e) {
    dispatch({ type: FETCH_FAILED, payload: get(e, "response.data", {}) });

    NotificationService.addError(`Could not fetch invoices. ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchAllInvoicesIds = (params, service = INVOICE_SERVICES.INTERNAL_INVOICES) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService, resourceType;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
    resourceType = INTERNAL_RESOURCE_TYPE;
  }

  dispatch({ type: FETCH_IDS_START, payload: {}, isFetchingInProgress: true });

  try {
    const result = await invoiceService.allIds(tenantSlug, params);
    const data = normalize(result.data, { endpoint: resourceType });

    dispatch({
      type: FETCH_IDS_SUCCESS,
      payload: data
    });
  } catch (e) {
    dispatch({ type: FETCH_FAILED, payload: get(e, "response.data", {}) });

    NotificationService.addError(`Could not fetch invoices ids. ${NotificationService.handleMessage(e)}`);
  }
};

export const bulkApproveInvoices = (
  references,
  isAllRecordsSelected,
  params = {},
  service = INVOICE_SERVICES.INTERNAL_INVOICES
) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: isAllRecordsSelected });

  const approveInvoiceResponses = Promise.allSettled(
    references.map(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
      return invoiceService.approve(tenantSlug, invoiceId, params);
    })
  );

  approveInvoiceResponses.then(results => {
    const failures = results.filter(response => response.status === "rejected");

    if (failures.length > 0) {
      if (!isAllRecordsSelected) {
        failures.forEach(failure => {
          handleBulkErrorMessage(failure, "approving", dispatch, service);
        });
      } else {
        NotificationService.addError(`Approving ${failures.length} of ${references.length} invoice(s) failed!`, {
          autoClose: false
        });
      }
    } else {
      NotificationService.addSuccess(`Successfully approved  ${references.length} invoice(s)`, { autoClose: false });
    }

    dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: false });
  });
};

export const bulkApproveSkipPayment = (references, isAllRecordsSelected, service = INVOICE_SERVICES.INTERNAL_INVOICES) =>
  bulkApproveInvoices(references, isAllRecordsSelected, { skipPayment: true }, service);

export const bulkCloseInvoices = (
  references,
  transitionOnly,
  isAllRecordsSelected,
  service = INVOICE_SERVICES.INTERNAL_INVOICES
) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: isAllRecordsSelected });

  const closeInvoiceResponses = Promise.allSettled(
    references.map(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
      return invoiceService.close(tenantSlug, invoiceId, { transitionOnly });
    })
  );

  closeInvoiceResponses.then(results => {
    const failures = results.filter(response => response.status === "rejected");

    if (failures.length > 0) {
      if (!isAllRecordsSelected) {
        failures.forEach(failure => {
          handleBulkErrorMessage(failure, "closing", dispatch, service);
        });
      } else {
        NotificationService.addError(`Closing ${failures.length} of ${references.length} invoice(s) failed!`, {
          autoClose: false
        });
      }
    } else {
      NotificationService.addSuccess(`Successfully closed ${references.length} invoice(s)`, { autoClose: false });
    }

    dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: false });
  });
};

export const bulkPayInvoices = (
  references,
  isAllRecordsSelected,
  params = {},
  service = INVOICE_SERVICES.INTERNAL_INVOICES
) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: isAllRecordsSelected });

  const payInvoiceResponses = Promise.allSettled(
    references.map(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
      return invoiceService.pay(tenantSlug, invoiceId, params);
    })
  );

  payInvoiceResponses.then(results => {
    const failures = results.filter(response => response.status === "rejected");
    const action = params.skipPayment ? "skipping payment" : "paying";

    if (failures.length > 0) {
      if (!isAllRecordsSelected) {
        failures.forEach(failure => {
          handleBulkErrorMessage(failure, action, dispatch, service);
        });
      } else {
        NotificationService.addError(
          `${params.skipPayment ? "Skipping payment" : "Paying"} ${failures.length} of ${
            references.length
          } invoice(s) failed!`,
          { autoClose: false }
        );
      }
    } else {
      const action = params.skipPayment ? "skipped payment" : "initiated payment";
      NotificationService.addSuccess(`Successfully ${action} for ${references.length} invoice(s)`, { autoClose: false });
    }

    dispatch({ type: BACKEND_PROCESSING_ONGOING, isBackendProcessingOngoing: false });
  });
};

export const bulkSkipPaymentForInvoices = (references, isAllRecordsSelected, service = INVOICE_SERVICES.INTERNAL_INVOICES) =>
  bulkPayInvoices(references, isAllRecordsSelected, { skipPayment: true }, service);

export const bulkExportInvoices = (
  references,
  isAllRecordsSelected,
  params = {},
  service = INVOICE_SERVICES.INTERNAL_INVOICES
) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  try {
    references.forEach(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
    });

    await invoiceService.export(tenantSlug, references, params);
    NotificationService.addSuccess(
      `Export started for ${references.length} invoice(s). File will soon be availabe for download`
    );
  } catch (err) {
    handleBulkErrorMessage(
      { reason: { ...err.response, response: { data: err.response.data } } },
      "exporting",
      dispatch,
      service
    );
  }
};

export const bulkNetsuiteApiExportInvoices = (
  references,
  service = INVOICE_SERVICES.INTERNAL_INVOICES,
  isAllRecordsSelected
) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  try {
    references.forEach(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
    });

    await invoiceService.netsuiteApiExport(tenantSlug, references);
    NotificationService.addSuccess(`Netsuite API export started for ${references.length} invoice(s)`);
  } catch (err) {
    handleBulkErrorMessage(
      { reason: { ...err.response, response: { data: err.response.data } } },
      "netsuite api export",
      dispatch,
      service
    );
  }
};

export const bulkVerifyPaymentInvoices = (references, isAllRecordsSelected, params = {}, service) => async (
  dispatch,
  getState
) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  try {
    references.forEach(invoiceId => {
      if (!isAllRecordsSelected) dispatch(markInvoiceAsProcessed(invoiceId));
    });

    await invoiceService.verifyPayment(tenantSlug, references, params);
    NotificationService.addSuccess(`Checking payment status for ${references.length} invoice(s).`);
  } catch (err) {
    handleBulkErrorMessage(
      { reason: { ...err.response, response: { data: err.response.data } } },
      "verifying payment",
      dispatch,
      service
    );
  }
};

export const bulkSimulateExportInvoices = (references, isAllRecordsSelected, service = INVOICE_SERVICES.INTERNAL_INVOICES) =>
  bulkExportInvoices(references, isAllRecordsSelected, { simulate: true }, service);

export const approveInvoice = (id, service = INVOICE_SERVICES.INTERNAL_INVOICES) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  try {
    await invoiceService.approve(tenantSlug, id);
    NotificationService.addSuccess("Invoice was successfully approved");
    dispatch(markInvoiceAsApproved(id));
  } catch (e) {
    NotificationService.addError(`Could not approve invoice. ${NotificationService.handleMessage(e)}`);
  }
};

export const closeInvoice = (id, service = INVOICE_SERVICES.INTERNAL_INVOICES) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  let invoiceService;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    invoiceService = InternalInvoicesService;
  }

  try {
    await invoiceService.close(tenantSlug, id);
    NotificationService.addSuccess("Invoice was successfully closed");
    dispatch(markInvoiceAsClosed(id));
  } catch (e) {
    NotificationService.addError(`Could not close invoice. ${NotificationService.handleMessage(e)}`);
  }
};

export const markInvoiceAsApproved = id => dispatch => {
  dispatch({ type: UPDATE_RESOURCE_FIELDS, id, payload: { status: INVOICE_STATUS.APPROVED_BY_USER } });
};

export const markInvoiceAsClosed = id => dispatch => {
  dispatch({ type: UPDATE_RESOURCE_FIELDS, id, payload: { status: INVOICE_STATUS.CLOSED_BY_USER } });
};

export const markInvoiceAsProcessed = id => dispatch => {
  dispatch({ type: UPDATE_RESOURCE_FIELDS, id, payload: { status: INVOICE_STATUS.PROCESSED_BY_USER } });
};

export const markInvoiceAsUnprocessed = id => dispatch => {
  dispatch({ type: UPDATE_RESOURCE_FIELDS, id, payload: { status: INVOICE_STATUS.PROCESS_ERROR } });
};

export const handleBulkErrorMessage = (failure, action, dispatch, service) => {
  let matcher;
  if (service === INVOICE_SERVICES.INTERNAL_INVOICES) {
    matcher = /\/internal_invoices\/?(.*)\//;
  }

  const id = failure.reason.request.responseURL.match(matcher)[1];
  const error = get(failure, "reason.response.data.errors[0].detail", null);

  if (id) {
    dispatch(markInvoiceAsUnprocessed(id));
  }

  const genericError = `Received '${failure.reason.request.statusText}' while ${action} invoice ${id}`;
  NotificationService.addError(error ? (id ? `(${id}) ${error}` : error) : genericError);
};
